import { requireExhaustive } from "../../shared/src/util";
import { TOwnershipStatus } from "../codecs/UserCaseUserType";
import { TOnboardingJourney } from "./onboardingJourney";
import { TOnboardingRoleAudience } from "./onboardingRoleAudience";

export type TCopyRun = {
    text: string;
    bold: boolean;
};

export type TCopyLine = Array<TCopyRun>;

export type TOnboardingCompleteContent = {
    title: string;
    leadIn: TCopyLine;
    teamBlock: Array<TCopyLine>;
    whatHappensNextHeading: string;
    journeySteps: Array<TCopyLine>;
    ownershipLine: TCopyLine | null;
    waitingOnOthersLine: TCopyLine | null;
    roleBlock: TCopyLine | null;
    outro: TCopyLine;
    emailSubject: string;
};

export const parseBoldMarkup = (source: string): TCopyLine =>
    source
        .split("**")
        .map((segment, index): TCopyRun => ({ text: segment, bold: index % 2 === 1 }))
        .filter((run) => run.text.length > 0);

const lines = (sources: Array<string>): Array<TCopyLine> => sources.map(parseBoldMarkup);

const TITLE = "That's everything we need — thank you.";

const LEAD_IN =
    "You have finished all your steps. **From here, we take over the legal work.** Here is a quick rundown of **what happens next**.";

const teamBlock = (caseHandlerFirstName: string | null): Array<TCopyLine> => {
    const handler = caseHandlerFirstName && caseHandlerFirstName.trim().length > 0
        ? caseHandlerFirstName.trim()
        : "your case handler";
    return lines([
        "**Your part is done — the onboarding team is still working** behind the scenes. The law asks us to finish checks and paperwork before the legal work can move, so **they may still be in touch** with a few small things.",
        `Once your case is fully ready, **your case handler, ${handler}, takes over**. They do the day-to-day legal work and become **your main contact**.`,
    ]);
};

const journeySteps = (journey: TOnboardingJourney): Array<string> => {
    switch (journey) {
        case "sale":
            return [
                "We put together your contract pack and send it to the buyer's solicitor.",
                "The buyer's side will ask questions. We answer most of them ourselves. A few may need your help.",
                "Most sales are ready to exchange in 4–8 weeks from this point. Exchange makes the sale legally binding.",
                "Completion usually follows 1–2 weeks after exchange. We pay off any mortgage, then send the rest of the money to you.",
            ];
        case "probate_sale":
            return [
                "We check the probate papers that give the estate the right to sell. If the grant of probate has not come through yet, waiting for it is usually the longest part.",
                "We put together the contract pack and send it to the buyer's solicitor.",
                "The buyer's side will ask questions. We answer most of them ourselves. A few may need your help.",
                "Once the grant is in, most sales are ready to exchange in 4–8 weeks. On completion, the sale money is paid to the estate.",
            ];
        case "divorce_sale":
            return [
                "We put together your contract pack and send it to the buyer's solicitor.",
                "We check the court order or agreement, so the sale money is split the way it says.",
                "The buyer's side will ask questions. We answer most of them ourselves. A few may need your help.",
                "Most sales are ready to exchange in 4–8 weeks from this point. On completion we pay off any mortgage and send each person their share.",
            ];
        case "purchase":
            return [
                "We order your searches and check the contract pack, the title, and the results. This usually takes 4–8 weeks before we are ready to exchange.",
                "We raise questions with the seller's solicitor and report anything important to you.",
                "You sign the contract and send us your deposit when we ask for it.",
                "We exchange, then complete — usually 1–2 weeks later. Then the keys are yours.",
            ];
        case "transfer":
            return [
                "We prepare the deed that moves the property into the right name(s).",
                "If there is a mortgage, we work with the lender to get their consent. This is often the slowest step.",
                "Everyone signs the deed, and we complete the transfer. Most transfers complete in 4–6 weeks from this point.",
                "We register the change at the Land Registry. The Land Registry can take several months — that is normal, and you are protected in the meantime.",
            ];
        case "divorce_transfer":
            return [
                "We prepare the deed that moves the property into the right name(s).",
                "We check the court order or agreement matches what the transfer says.",
                "If there is a mortgage, we work with the lender to get their consent. This is often the slowest step.",
                "Everyone signs and we complete the transfer — most complete in 4–6 weeks from this point. Then we register the change at the Land Registry.",
            ];
        case "probate_transfer":
            return [
                "We check the probate papers that give the estate the right to transfer the property. If the grant has not come through yet, waiting for it is usually the longest part.",
                "We prepare the deed that moves the property from the estate to the new owner(s).",
                "Everyone signs the deed, and we complete the transfer.",
                "We register the new owner(s) at the Land Registry. The Land Registry can take several months — that is normal.",
            ];
        case "remortgage":
            return [
                "We check your new mortgage offer and your lender's conditions.",
                "We order any searches your new lender needs.",
                "You sign the mortgage deed when we send it to you.",
                "On completion day we pay off your old mortgage and your new one starts. Most remortgages complete in 4–6 weeks from this point.",
            ];
        case "other":
            return [
                "We check everything you have sent us.",
                "We get on with the legal work for your case.",
                "We email you when there is news, or if we need anything from you.",
            ];
        default:
            return requireExhaustive(journey);
    }
};

const ownershipLine = (journey: TOnboardingJourney, ownershipStatus: TOwnershipStatus): string | null => {
    const isTransfer = journey === "transfer" || journey === "divorce_transfer" || journey === "probate_transfer";
    if (!isTransfer) {
        return null;
    }
    switch (ownershipStatus) {
        case "new_owner":
            return "When the transfer completes, the property will be registered in your name.";
        case "remaining_owner":
            return "When the transfer completes, you stay on the title as an owner.";
        case "departing_owner":
            return "When the transfer completes, your name comes off the title. We will confirm with you once it is done.";
        case "not_an_owner":
        case "unknown":
            return null;
        default:
            return requireExhaustive(ownershipStatus);
    }
};

const WAITING_ON_OTHERS =
    "Some people on your case still have their own steps to finish. **We are chasing them — you do not need to do anything.**";

const roleBlock = (roleAudience: TOnboardingRoleAudience | null): string | null => {
    if (roleAudience === null) {
        return null;
    }
    switch (roleAudience) {
        case "driver":
            return "**We may still need small things** from you along the way — a signature, a document, or a quick answer. **We will always email you** when we do, and we keep these asks as rare as we can.";
        case "representative":
            return "We know acting for someone else — or for an estate — can be a lot. **We only contact you when we truly need something**: usually a signature or a quick answer. **Everything else, we handle.**";
        case "giftor":
            return "Your part is **nearly done**. We may still ask for proof of where the gift money came from — the law asks us to check this. After that, there is **nothing more for you to do**.";
        case "beneficiary":
            return "There is **nothing you need to do right now**. We check your bank details carefully before any money is sent, and **we will contact you when a payment is on its way**.";
        default:
            return requireExhaustive(roleAudience);
    }
};

const OUTRO =
    "You can **log back in any time** to see where things are. If we need anything else, **we will email you** — you never need to chase us.";

const EMAIL_SUBJECT = "Your steps are done — here's what happens next";

export type TOnboardingCompleteContentInput = {
    journey: TOnboardingJourney;
    roleAudience: TOnboardingRoleAudience | null;
    ownershipStatus: TOwnershipStatus;
    caseHandlerFirstName: string | null;
    othersHaveIncompleteSteps: boolean;
};

export const composeOnboardingCompleteContent = (input: TOnboardingCompleteContentInput): TOnboardingCompleteContent => {
    const ownership = ownershipLine(input.journey, input.ownershipStatus);
    const role = roleBlock(input.roleAudience);
    return {
        title: TITLE,
        leadIn: parseBoldMarkup(LEAD_IN),
        teamBlock: teamBlock(input.caseHandlerFirstName),
        whatHappensNextHeading: "What happens next",
        journeySteps: lines(journeySteps(input.journey)),
        ownershipLine: ownership === null ? null : parseBoldMarkup(ownership),
        waitingOnOthersLine: input.othersHaveIncompleteSteps ? parseBoldMarkup(WAITING_ON_OTHERS) : null,
        roleBlock: role === null ? null : parseBoldMarkup(role),
        outro: parseBoldMarkup(OUTRO),
        emailSubject: EMAIL_SUBJECT,
    };
};
